import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import ModuleContext from '../../../_contexts/ModuleContext';

import Button, { ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import SlideSetIcon from '@pelckmans/elementis/icons/material-presentation-present-modus';

import { SlideSet } from '../../../../../api/slide-set';
import useModuleLicenseCheck from '../../../../../hooks/useModuleLicenseCheck';
import { MaterialIcon } from '../../../components/MaterialIcon';
import useSlideSets from '../../../hooks/useSlideSets';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';

export default function SlideSetAppButton({ size = 'md' }: { size?: ButtonProps['size'] }) {
  const { module } = useContext(ModuleContext);
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { t } = useTranslation();
  const { slideSets, buildDoceoUrl } = useSlideSets();
  const { isUserLicensed } = useModuleLicenseCheck();

  if (!module) return null;
  if (!module.enableSlideSetsAccess) return null;

  const tooltipText = !isUserLicensed ? t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.NO_LICENSE_TOOLTIP') : undefined;

  const sharedSlideSets = (sharedMaterials ?? []).filter(
    material => material.materialType === 'slide-set',
  ) as SlideSet[];

  const sortedSlideSets = [...slideSets, ...sharedSlideSets].sort(
    (a, b) => Number(Boolean(a.userId)) - Number(Boolean(b.userId)),
  );

  if (sortedSlideSets.length === 0) return null;
  if (sortedSlideSets.length === 1) {
    return (
      <Button
        key="slide-set-button"
        as="link"
        startIcon={<SlideSetIcon data-testid="slide-set-icon" />}
        prominence="secondary"
        size={size}
        target="_blank"
        rel="noopener noreferrer"
        href={buildDoceoUrl(sortedSlideSets[0])}
        tooltip={tooltipText}
        disabled={!isUserLicensed}
      >
        {t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.OPEN_SLIDE_SET')}
      </Button>
    );
  }
  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.OPEN_SLIDE_SET')}
      key="slide-set-button"
      buttonProps={{
        prominence: 'secondary',
        startIcon: <SlideSetIcon data-testid="slide-set-icon" />,
        disabled: !isUserLicensed,
      }}
      size={size}
      zIndex={4}
      tooltip={tooltipText}
    >
      {sortedSlideSets.map(slideSet => {
        return (
          <ButtonDropdown.Item
            as="link"
            key={`${slideSet.id}-${slideSet.versionId}`}
            value={slideSet.id}
            text={slideSet.name}
            href={buildDoceoUrl(slideSet)}
            target="_blank"
            rel="noopener noreferrer"
            textIcon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
          />
        );
      })}
    </ButtonDropdown>
  );
}
